import React, { useMemo, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
  ResponsiveContainer, BarChart, Bar, Cell, Legend,
} from 'recharts';
import { motion } from 'motion/react';
import { getUnsoldRows, getIncludedSold, getTotalProfit, getLivePrice } from './helpers';
import { findSkinImage } from './skinIndex';

// Analytics 2 — the same numbers as Analytics, but told as a story: every
// section says what it shows, how to read it, and where the data comes from.
const AXIS_TICK = '#9196aa';
const GRID      = 'rgba(255,255,255,0.06)';

function getItemTypes(row) {
  if (!row?.itemType) return [];
  return Array.isArray(row.itemType) ? row.itemType : [row.itemType];
}


const fmt = (n) => Math.abs(n).toFixed(2);
const RANGES = [
  { key: 'all', label: 'All time', days: null },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '7d',  label: 'Last 7 days', days: 7 },
];

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const v = payload[0].value;
  return (
    <div className="custom-tooltip">
      <div className="ct-label">{label}</div>
      <div className={`ct-value ${v < 0 ? 'red' : ''}`}>{v >= 0 ? '+' : ''}${v.toFixed(2)}</div>
    </div>
  );
};

function Info({ text }) {
  return <span className="an2-info" title={text} aria-label={text}>i</span>;
}

function Section({ num, title, scope, explain, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="an2-sec">
        <span className="num">{num}</span>
        <h2>{title}</h2>
        <span className="an2-scope">{scope}</span>
      </div>
      <p className="an2-explain">{explain}</p>
      {children}
    </motion.section>
  );
}

export default function AnalyticsTab2({ rows, settings, livePrices, skinsData = [] }) {
  const [rangeKey, setRangeKey] = useState('all');
  const range = RANGES.find(r => r.key === rangeKey);

  const isExcluded = (r) => {
    const types = getItemTypes(r);
    const t = types.length ? settings?.itemTypes?.find(it => it.value === types[0].value) : null;
    return t?.overrideProfitLoss === true;
  };

  // Sold rows inside the selected range (range applies to *sales*).
  const rangeSold = useMemo(() => {
    let sold = getIncludedSold(rows || [], isExcluded).filter(r => r.skin?.value);
    if (range.days) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - range.days);
      sold = sold.filter(r => new Date(r.timestamp) >= cutoff);
    }
    return sold;
  }, [rows, range]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Headline figures ───────────────────────────────────────
  const figures = useMemo(() => {
    const profit    = rangeSold.reduce((s, r) => s + (parseFloat(r.profit) || 0), 0);
    const revenue   = rangeSold.reduce((s, r) => s + (parseFloat(r.sellPrice) || 0), 0);
    const soldCost  = rangeSold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    const wins      = rangeSold.filter(r => (parseFloat(r.profit) || 0) > 0).length;
    const winRate   = rangeSold.length ? Math.round(wins / rangeSold.length * 100) : null;
    const avgProfit = rangeSold.length ? profit / rangeSold.length : null;
    const roi       = soldCost > 0 ? profit / soldCost * 100 : null;

    // Current holdings — always "right now", independent of range.
    const unsold = getUnsoldRows(rows || []);
    const holdCost = unsold.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
    let holdValue = 0, priced = 0;
    unsold.forEach(r => {
      const p = getLivePrice(livePrices, r)?.starting_at?.price;
      if (p != null) { holdValue += p; priced++; }
      else holdValue += parseFloat(r.buyPrice) || 0;
    });
    const totalInvested = (rows || []).filter(r => r.skin?.value)
      .reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);

    return {
      profit, revenue, soldCost, winRate, wins, avgProfit, roi,
      soldCount: rangeSold.length,
      lifetimeProfit: getTotalProfit(rows || [], isExcluded),
      unsoldCount: unsold.length, holdCost, holdValue,
      holdPnl: holdValue - holdCost, priced, totalInvested,
    };
  }, [rangeSold, rows, livePrices]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cumulative profit (all time) ───────────────────────────
  const cumulative = useMemo(() => {
    const sold = getIncludedSold(rows || [], isExcluded)
      .filter(r => r.skin?.value)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    let cum = 0;
    return sold.map(r => {
      cum += parseFloat(r.profit) || 0;
      return {
        date: new Date(r.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        profit: parseFloat(cum.toFixed(2)),
      };
    });
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Monthly P&L (all time) ─────────────────────────────────
  const monthly = useMemo(() => {
    const byMonth = {};
    getIncludedSold(rows || [], isExcluded).filter(r => r.skin?.value).forEach(r => {
      const d = new Date(r.timestamp);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth[key]) byMonth[key] = {
        key, label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), profit: 0, count: 0,
      };
      byMonth[key].profit += parseFloat(r.profit) || 0;
      byMonth[key].count  += 1;
    });
    return Object.values(byMonth)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map(m => ({ ...m, profit: parseFloat(m.profit.toFixed(2)) }));
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const bestMonth  = useMemo(() => monthly.reduce((b, m) => !b || m.profit > b.profit ? m : b, null), [monthly]);
  const worstMonth = useMemo(() => monthly.reduce((w, m) => !w || m.profit < w.profit ? m : w, null), [monthly]);

  // ── Holdings value vs cost over time ───────────────────────
  const holdingsHistory = useMemo(() => {
    // Same definition of "held" as every other total on the page — a
    // traded-away row has an empty sellPrice forever and is not a holding.
    const unsold = getUnsoldRows(rows || []);
    if (!unsold.length) return [];
    const today = new Date().toISOString().slice(0, 10);
    const dateSet = new Set([today]);
    unsold.forEach(r => {
      if (r.timestamp) dateSet.add(new Date(r.timestamp).toISOString().slice(0, 10));
      (r.priceHistory || []).forEach(p => dateSet.add(p.date));
    });
    const dates = [...dateSet].sort();
    const getPrice = (row, date) => {
      const buyDate = row.timestamp ? new Date(row.timestamp).toISOString().slice(0, 10) : null;
      if (buyDate && date < buyDate) return null;
      if (date === today) {
        const live = getLivePrice(livePrices, row)?.starting_at?.price;
        if (live != null) return live;
      }
      const snaps = (row.priceHistory || []).filter(p => p.date <= date);
      if (snaps.length) {
        const latest = snaps[snaps.length - 1];
        return latest.buffPrice ?? latest.price ?? null;
      }
      if (buyDate && date >= buyDate) return parseFloat(row.buyPrice) || null;
      return null;
    };
    return dates.map(date => {
      let value = 0, cost = 0, counted = 0;
      unsold.forEach(r => {
        const p = getPrice(r, date);
        if (p == null) return;
        value += p;
        cost  += parseFloat(r.buyPrice) || 0;
        counted++;
      });
      return counted ? {
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: parseFloat(value.toFixed(2)),
        cost:  parseFloat(cost.toFixed(2)),
      } : null;
    }).filter(Boolean);
  }, [rows, livePrices]);

  // ── Winners / losers (in range) ────────────────────────────
  const winners = useMemo(() =>
    [...rangeSold].filter(r => (parseFloat(r.profit) || 0) > 0)
      .sort((a, b) => parseFloat(b.profit) - parseFloat(a.profit)).slice(0, 5), [rangeSold]);
  const losers = useMemo(() =>
    [...rangeSold].filter(r => (parseFloat(r.profit) || 0) < 0)
      .sort((a, b) => parseFloat(a.profit) - parseFloat(b.profit)).slice(0, 5), [rangeSold]);

  // ── Category scoreboard (all time) ─────────────────────────
  const categories = useMemo(() => {
    if (!settings?.itemTypes || !rows) return [];
    return settings.itemTypes.map(type => {
      const match = r => getItemTypes(r).some(t => t.value === type.value);
      const all = rows.filter(r => match(r) && r.skin?.value);
      const sold = all.filter(r => parseFloat(r.sellPrice) > 0);
      const profit = sold.reduce((s, r) => s + (parseFloat(r.profit) || 0), 0);
      const invested = all.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0);
      return { name: type.label, color: type.color || '#818cf8', profit, invested, soldCount: sold.length, total: all.length };
    }).filter(c => c.invested > 0).sort((a, b) => b.profit - a.profit);
  }, [rows, settings]);

  const profC = settings?.profitTextColor || 'var(--green)';
  const lossC = settings?.lossTextColor   || 'var(--red)';
  const pcol  = (v) => v >= 0 ? profC : lossC;

  const noData = !rows?.filter(r => r.skin?.value).length;
  if (noData) {
    return (
      <div className="an2-root" style={{ textAlign: 'center', padding: '80px 24px' }}>
        <h1 style={{ fontSize: '1.26rem', color: 'var(--text-muted)' }}>Nothing to analyse yet</h1>
        <p style={{ fontSize: '0.98rem', color: 'var(--text-dim)' }}>Add and sell a few items in the Live tab first.</p>
      </div>
    );
  }

  const scopeLabel = range.days ? range.label.toUpperCase() : 'ALL TIME';
  const up = figures.holdPnl >= 0;

  return (
    <div className="an2-root">

      {/* ── Header ─────────────────────────────────── */}
      <div className="an2-header">
        <div>
          <h1>Analytics, in plain English</h1>
          <p>The same numbers as Analytics, explained as you go. Pick a period; it applies to everything marked “{scopeLabel}”.</p>
        </div>
        <div className="an2-range">
          {RANGES.map(r => (
            <button key={r.key} className={rangeKey === r.key ? 'on' : ''} onClick={() => setRangeKey(r.key)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── The story ──────────────────────────────── */}
      <motion.div className="an2-story" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        Over all time you've put <b>${fmt(figures.totalInvested)}</b> into skins.
        {' '}{range.days ? `In the ${range.label.toLowerCase()}` : 'So far'}, you sold <b>{figures.soldCount}</b> item{figures.soldCount !== 1 ? 's' : ''} for{' '}
        <b>${fmt(figures.revenue)}</b>, which {figures.profit >= 0
          ? <>earned you <span className="good">+${fmt(figures.profit)}</span> in profit</>
          : <>lost you <span className="bad">−${fmt(figures.profit)}</span></>}.
        {' '}You still hold <b>{figures.unsoldCount}</b> item{figures.unsoldCount !== 1 ? 's' : ''} that cost <b>${fmt(figures.holdCost)}</b> and{' '}
        {figures.priced > 0 ? <>are worth about <b>${fmt(figures.holdValue)}</b> today, so {up
          ? <>you'd pocket an extra <span className="good">+${fmt(figures.holdPnl)}</span> if you sold everything now</>
          : <>you'd take a <span className="bad">−${fmt(figures.holdPnl)}</span> hit if you sold everything now</>}</>
          : <>are waiting for live prices</>}.
      </motion.div>

      {/* ── 01 · Key numbers ───────────────────────── */}
      <Section num="01" title="The key numbers" scope={scopeLabel}
        explain="Each card is one question about your trading, answered. Hover the ⓘ to see exactly how it's calculated.">
        <div className="an2-kpis">
          <div className="an2-kpi">
            <div className="top"><span className="lbl">Money actually made</span>
              <Info text="Realized profit: for every item you sold in this period, sell price minus buy price minus fees, all added up. This is money in the bank, not paper value." /></div>
            <div className="val" style={{ color: pcol(figures.profit) }}>{figures.profit >= 0 ? '+' : '−'}${fmt(figures.profit)}</div>
            <div className="sub">from {figures.soldCount} completed sale{figures.soldCount !== 1 ? 's' : ''}</div>
          </div>
          <div className="an2-kpi">
            <div className="top"><span className="lbl">Return on the money used</span>
              <Info text="ROI: profit divided by what those sold items originally cost you. +20% means every $100 you spent came back as $120." /></div>
            <div className="val" style={{ color: figures.roi == null ? 'var(--text-dim)' : pcol(figures.roi) }}>
              {figures.roi == null ? 'n/a' : `${figures.roi >= 0 ? '+' : '−'}${Math.abs(figures.roi).toFixed(1)}%`}
            </div>
            <div className="sub">{figures.roi == null ? 'no sales in this period' : `on $${fmt(figures.soldCost)} spent`}</div>
          </div>
          <div className="an2-kpi">
            <div className="top"><span className="lbl">How often you win</span>
              <Info text="Win rate: the share of your sales that made any profit at all. Above 50% means you profit more often than not." /></div>
            <div className="val">{figures.winRate == null ? 'n/a' : `${figures.winRate}%`}</div>
            <div className="sub">{figures.winRate == null ? 'no sales in this period' : `${figures.wins} of ${figures.soldCount} sales made money`}</div>
          </div>
          <div className="an2-kpi">
            <div className="top"><span className="lbl">Typical sale</span>
              <Info text="Average profit per sale: total profit divided by number of sales. One big win can pull this up, so check the winners list below." /></div>
            <div className="val" style={{ color: figures.avgProfit == null ? 'var(--text-dim)' : pcol(figures.avgProfit) }}>
              {figures.avgProfit == null ? 'n/a' : `${figures.avgProfit >= 0 ? '+' : '−'}$${fmt(figures.avgProfit)}`}
            </div>
            <div className="sub">average per completed sale</div>
          </div>
          <div className="an2-kpi">
            <div className="top"><span className="lbl">Profit still on the table</span>
              <Info text="Unrealized P/L: what your current holdings are worth at today's Buff163 prices, minus what you paid. It becomes real profit only when you sell." /></div>
            <div className="val" style={{ color: pcol(figures.holdPnl) }}>{up ? '+' : '−'}${fmt(figures.holdPnl)}</div>
            <div className="sub">across {figures.unsoldCount} items you still hold (right now)</div>
          </div>
        </div>
      </Section>

      {/* ── 02 · Profit journey ────────────────────── */}
      <Section num="02" title="Your profit journey" scope="ALL TIME"
        explain="Every sale you've ever made, in order. Each one nudges the line up (profit) or down (loss). Up and to the right is the goal; the dashed line is break-even.">
        <div className="an2-panel">
          <div style={{ padding: '16px 10px 4px' }}>
            <ResponsiveContainer width="100%" height={230}>
              <AreaChart data={cumulative} margin={{ top: 6, right: 18, left: -4, bottom: 0 }}>
                <defs>
                  <linearGradient id="an2Cume" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4ade80" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#4ade80" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="date" tick={{ fill: AXIS_TICK, fontSize: 14.5 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fill: AXIS_TICK, fontSize: 14.5 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} width={56} />
                <Tooltip content={<ChartTip />} cursor={{ stroke: 'rgba(255,255,255,0.14)' }} />
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" strokeDasharray="5 4" />
                <Area type="monotone" dataKey="profit" stroke="#4ade80" strokeWidth={2.5} fill="url(#an2Cume)" dot={false}
                  activeDot={{ fill: '#4ade80', stroke: 'var(--panel)', strokeWidth: 2, r: 5 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="an2-reading">
            <b>How to read it:</b> the line ends at <b style={{ color: pcol(figures.lifetimeProfit) }}>
            {figures.lifetimeProfit >= 0 ? '+' : '−'}${fmt(figures.lifetimeProfit)}</b>, your total profit from every sale ever.
            Flat stretches are periods where you didn't sell anything.
          </div>
        </div>
      </Section>

      {/* ── 03 · Month by month ────────────────────── */}
      <Section num="03" title="Month by month" scope="ALL TIME"
        explain="Total profit from sales in each calendar month. Green above the line means the month made money; red below means it lost money.">
        <div className="an2-panel">
          <div style={{ padding: '16px 10px 4px' }}>
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={monthly} margin={{ top: 6, right: 18, left: -4, bottom: 0 }} barCategoryGap="30%">
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="label" tick={{ fill: AXIS_TICK, fontSize: 14.5 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: AXIS_TICK, fontSize: 14.5 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} width={56} />
                <Tooltip content={<ChartTip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" />
                <Bar dataKey="profit" animationDuration={800}>
                  {monthly.map((m, i) => (
                    <Cell key={i} fill={m.profit >= 0 ? '#4ade80' : '#f87171'}
                      radius={m.profit >= 0 ? [6, 6, 0, 0] : [0, 0, 6, 6]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {bestMonth && worstMonth && (
            <div className="an2-reading">
              <b>How to read it:</b> your best month was <b>{bestMonth.label}</b>{' '}
              (<b style={{ color: profC }}>+${fmt(bestMonth.profit)}</b> across {bestMonth.count} sales)
              {worstMonth.profit < 0 && <>; your toughest was <b>{worstMonth.label}</b>{' '}
              (<b style={{ color: lossC }}>−${fmt(worstMonth.profit)}</b>)</>}.
            </div>
          )}
        </div>
      </Section>

      {/* ── 04 · What you hold, over time ──────────── */}
      {holdingsHistory.length >= 2 && (
        <Section num="04" title="What your holdings are worth" scope="CURRENT ITEMS"
          explain="Two lines for the items you haven't sold: what you paid for them (the dashed line) and what they're worth at market price. The gap between them is your paper profit, or paper loss.">
          <div className="an2-panel">
            <div style={{ padding: '16px 10px 4px' }}>
              <ResponsiveContainer width="100%" height={230}>
                <AreaChart data={holdingsHistory} margin={{ top: 6, right: 18, left: -4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="an2Hold" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#818cf8" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="#818cf8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: AXIS_TICK, fontSize: 14.5 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: AXIS_TICK, fontSize: 14.5 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} width={56} />
                  <Tooltip
                    contentStyle={{ background: 'var(--panel-2)', border: '1px solid var(--border-hover)', borderRadius: 8, fontSize: '0.95rem' }}
                    formatter={(v, name) => [`$${parseFloat(v).toFixed(2)}`, name]} />
                  <Legend iconType="plainline" wrapperStyle={{ fontSize: '0.92rem', paddingTop: 6 }} />
                  <Area type="monotone" name="What you paid (cost)" dataKey="cost" stroke="#f59e0b" strokeWidth={1.5}
                    strokeDasharray="5 4" fill="none" dot={false} />
                  <Area type="monotone" name="What it's worth (market)" dataKey="value" stroke="#818cf8" strokeWidth={2.5}
                    fill="url(#an2Hold)" dot={false} activeDot={{ fill: '#818cf8', stroke: 'var(--panel)', strokeWidth: 2, r: 5 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="an2-reading">
              <b>How to read it:</b> right now the market line sits{' '}
              <b style={{ color: pcol(figures.holdPnl) }}>{up ? 'above' : 'below'}</b> the cost line by{' '}
              <b style={{ color: pcol(figures.holdPnl) }}>${fmt(figures.holdPnl)}</b>.{' '}
              {up ? "That's what you'd gain by selling everything today." : "That's how far your holdings are underwater today."}
            </div>
          </div>
        </Section>
      )}

      {/* ── 05 · Best & worst ──────────────────────── */}
      <Section num="05" title="Your best and worst sales" scope={scopeLabel}
        explain="The five sales that made you the most, and the five that cost you the most. If one item dominates a list, it's shaping most of your averages above.">
        <div className="an2-wl">
          <div className="an2-wl-col">
            <div className="an2-wl-title" style={{ color: profC }}>Made you the most</div>
            {winners.length === 0 && <div style={{ fontSize: '0.94rem', color: 'var(--text-dim)' }}>No profitable sales in this period.</div>}
            {winners.map((r, i) => {
              const img = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
              return (
                <div className="an2-wl-row" key={i}>
                  <span className="an2-wl-rank">{['🥇', '🥈', '🥉'][i] || `${i + 1}.`}</span>
                  {img && <img src={img} alt="" />}
                  <span className="an2-wl-name" title={r.skin?.value}>{r.skin?.value}</span>
                  <span className="an2-wl-amt" style={{ color: profC }}>+${fmt(parseFloat(r.profit) || 0)}</span>
                </div>
              );
            })}
          </div>
          <div className="an2-wl-col">
            <div className="an2-wl-title" style={{ color: lossC }}>Cost you the most</div>
            {losers.length === 0 && <div style={{ fontSize: '0.94rem', color: 'var(--text-dim)' }}>No losing sales in this period. Nice.</div>}
            {losers.map((r, i) => {
              const img = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
              return (
                <div className="an2-wl-row" key={i}>
                  <span className="an2-wl-rank" style={{ fontSize: '0.94rem', color: 'var(--text-dim)', fontWeight: 700 }}>{i + 1}.</span>
                  {img && <img src={img} alt="" />}
                  <span className="an2-wl-name" title={r.skin?.value}>{r.skin?.value}</span>
                  <span className="an2-wl-amt" style={{ color: lossC }}>−${fmt(parseFloat(r.profit) || 0)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ── 06 · Category scoreboard ───────────────── */}
      {categories.length > 0 && (
        <Section num="06" title="Which categories are working" scope="ALL TIME"
          explain="Everything you've bought, grouped by the categories you assigned. 'Earning' means its completed sales are net profitable; 'no sales yet' means the verdict is still out.">
          <div className="an2-panel">
            {categories.map((c, i) => {
              const verdict = c.soldCount === 0
                ? { label: 'no sales yet', bg: 'rgba(255,255,255,0.05)', color: 'var(--text-dim)' }
                : c.profit >= 0
                  ? { label: `earning · +$${fmt(c.profit)}`, bg: 'rgba(34,197,94,0.12)', color: profC }
                  : { label: `losing · −$${fmt(c.profit)}`, bg: 'rgba(248,113,113,0.12)', color: lossC };
              return (
                <div className="an2-cat-row" key={i}>
                  <span className="an2-cat-dot" style={{ background: c.color }} />
                  <span className="an2-cat-name">{c.name}</span>
                  <span className="an2-cat-num">{c.total} item{c.total !== 1 ? 's' : ''} · {c.soldCount} sold</span>
                  <span className="an2-cat-num">${fmt(c.invested)} invested</span>
                  <span className="an2-verdict" style={{ background: verdict.bg, color: verdict.color }}>{verdict.label}</span>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      <p style={{ margin: '30px 0 10px', fontSize: '0.92rem', color: 'var(--text-dim)', textAlign: 'center' }}>
        All prices come from Buff163 lowest asks · profit figures account for marketplace fees recorded on each sale.
      </p>
    </div>
  );
}
